import { declareParams, JsonApiRequestError } from '@farfetched/core';
import { zodContract } from '@farfetched/zod';
import { applyTimeZone, RegistryParams, RegistryQuery } from 'shared/lib';
import { createAuthQuery } from '../auth-http-client';
import { createPaginatedSchema, PlayerTransaction, playerTransactionSchema } from '../types';

export type OriginalPlayerTransactionRegistryParams = {
	page?: number;
	limit?: number;
	sortField?: string | null;
	sortDirection?: string;
	timeZone?: number;
	dateTo?: string;
	dateFrom?: string;
	playerIds?: string[];
	deviceIds?: string[];
	gameIds?: string[];
	cashdeskIds?: string[];
	cashierIds?: string[];
	accountIds?: string[];
	currencies?: string[];
	types?: string[];
};

export type PlayerTransactionRegistryFilters = {
	playerIds: number[];
	deviceIds: number[];
	gameIds: number[];
	cashdeskIds: number[];
	cashierIds: number[];
	accountIds: number[];
	currencies: string[];
	types: string[];
	dateFrom: string;
	dateTo: string;
	timeZone: number;
};

export type PlayerTransactionRegistryParams = RegistryParams<PlayerTransactionRegistryFilters>;

export type PlayerTransactionRegistryQuery = RegistryQuery<
	PlayerTransaction,
	PlayerTransactionRegistryFilters,
	JsonApiRequestError
>;

export const createPlayerTransactionRegistryQuery = (): PlayerTransactionRegistryQuery => {
	const query = createAuthQuery({
		params: declareParams<PlayerTransactionRegistryParams>(),
		request: {
			method: 'GET',
			url: '/transaction/player',
			query: (params): OriginalPlayerTransactionRegistryParams => ({
				page: params.page,
				limit: params.limit,
				sortField: params.sortField ?? undefined,
				sortDirection: params.sortDirection,
				timeZone: params.filters.timeZone,
				dateTo: params.filters.dateTo || undefined,
				dateFrom: params.filters.dateFrom || undefined,
				playerIds:
					params.filters.playerIds.length === 0 ? undefined : params.filters.playerIds.map(String),
				deviceIds:
					params.filters.deviceIds.length === 0 ? undefined : params.filters.deviceIds.map(String),
				gameIds:
					params.filters.gameIds.length === 0 ? undefined : params.filters.gameIds.map(String),
				cashdeskIds:
					params.filters.cashdeskIds.length === 0
						? undefined
						: params.filters.cashdeskIds.map(String),
				cashierIds:
					params.filters.cashierIds.length === 0
						? undefined
						: params.filters.cashierIds.map(String),
				accountIds: [...params.filters.accountIds.map(String)],
				currencies: params.filters.currencies.length === 0 ? undefined : params.filters.currencies,
				types: params.filters.types.length === 0 ? undefined : params.filters.types,
			}),
		},
		response: {
			contract: zodContract(createPaginatedSchema(playerTransactionSchema)),
			mapData: ({ result, params }) => ({
				...result,
				rows: result.rows.map<PlayerTransaction>((row) => ({
					...row,
					date: applyTimeZone(row.date, params.filters.timeZone),
				})),
			}),
		},
	});

	return query;
};
